import React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { AGENT_LAUNCH_CREATE_FLOW_DISMISSAL_STORAGE_KEY } from "@v2/features/agent-beta/agentLaunchDismissal";
import {
  EDITOR_AGENT_CALLOUT_DISMISSED_AT_STORAGE_KEY,
  EDITOR_V1_MIGRATION_BANNER_DISMISSED_STORAGE_KEY,
} from "@v2/features/agent-beta/editorAgentCalloutStorage";
import { useLocalStorage } from "@v2/hooks/useLocalStorage";

const DISMISSAL_RESETS = [
  {
    storageKey: AGENT_LAUNCH_CREATE_FLOW_DISMISSAL_STORAGE_KEY,
    title: "New Project Agent introduction",
    clearEffect: "Re-arms the introduction shown from the New Project flow.",
  },
  {
    storageKey: EDITOR_AGENT_CALLOUT_DISMISSED_AT_STORAGE_KEY,
    title: "Editor Agent callout",
    clearEffect: "Re-shows the onboarding callout inside the editor.",
  },
  {
    storageKey: EDITOR_V1_MIGRATION_BANNER_DISMISSED_STORAGE_KEY,
    title: "Editor Move to Replo Agent banner",
    clearEffect: "Re-shows the banner offering to move v1 pages into Agent.",
  },
] as const;

function DismissalReset({
  clearEffect,
  storageKey,
  title,
}: {
  clearEffect: string;
  storageKey: string;
  title: string;
}) {
  const storage = useLocalStorage();
  const isStored = storage.getItem(storageKey) !== null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-2.5">
      <div className="flex min-w-0 basis-64 grow flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-default">{title}</span>
          <Tag size="sm" variant={isStored ? "neutral" : "success"}>
            {isStored ? "Stored" : "Not stored"}
          </Tag>
        </div>
        <code className="break-all text-[11px] text-muted">{storageKey}</code>
        <span className="text-xs text-muted">
          Clear effect: {clearEffect} The page reloads so in-memory dismissal
          state is reset too.
        </span>
      </div>
      <Button
        size="sm"
        variant="secondary"
        disabled={!isStored}
        onClick={() => {
          storage.removeItem(storageKey);
          window.location.reload();
        }}
      >
        Clear and reload
      </Button>
    </div>
  );
}

export function LocalStorageTab() {
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-lg border border-cyan-border bg-cyan-soft p-2.5 text-xs text-default">
        Only the keys listed below are removed. Project, account,
        authentication, and other browser data stay untouched.
      </div>
      {DISMISSAL_RESETS.map((reset) => (
        <DismissalReset key={reset.storageKey} {...reset} />
      ))}
    </div>
  );
}
